import React, { ComponentProps, PropsWithChildren, useMemo } from 'react'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'

interface Props extends PropsWithChildren<ComponentProps<typeof Group>> {
  separator?: React.ReactNode
}

const DEFAULT_SEPARATOR = <BodyText variant="medium" colour="neutral-five" align="center">|</BodyText>

/**
 * A component that automatically inserts separators between rendered children.
 * Falsy children (null, undefined, false) are filtered out before separation.
 */
function GroupWithSeparator({
  children,
  separator = DEFAULT_SEPARATOR,
  ...rest
}: Props) {
  const childrenWithSeparators = useMemo(() => {
    const validChildren = React.Children.toArray(children).filter(Boolean)

    if (validChildren.length === 0) {
      return null
    }

    const result: Array<React.ReactNode> = []
    validChildren.forEach((child, index) => {
      result.push(child)
      if (index < validChildren.length - 1) {
        result.push(
          <React.Fragment key={`separator-${index}`}>
            {separator}
          </React.Fragment>
        )
      }
    })

    return result
  }, [children, separator])

  if (!childrenWithSeparators) {
    return null
  }

  return (
    <Group {...rest}>
      {childrenWithSeparators}
    </Group>
  )
}

export default GroupWithSeparator
